import React from 'react';
import { useParams } from 'react-router-dom';
import { useQueries, useMutation } from '@tanstack/react-query';

import ContentPage from 'components/ContentView/ContentPage/ContentPage';
import useAlerts from 'utils/hooks/useAlerts';
import createQueryRequest from 'services/createQueryRequest';
import useAPI from 'services/useAPI';
import { ResourceType } from 'utils/types';
import { org2assets } from './queries';
import RelationTable, { VisibleColumns } from 'components/RelationTable/RelationTable';
import useRelations from 'utils/hooks/useRelations';
import { useRelationData, createRelationQueryData, createOrgOwnerRelationQueryData } from './queries';
import { assetKeys } from 'components/Admin/Assets/queries';
import _ from 'lodash';
import { getFullProviderClientId, getProviderClientListFromAsset } from 'utils/asset';


const OrgToAssetRelations = ({ breadcrumbs }) => {
  const api = useAPI();
  const { orgId } = useParams();
  const [alerts,, addErrorAlert] = useAlerts();

  const [assetsQuery, assetsRelationQuery, assetsOwnerQuery] = useQueries({
    queries: [
      { queryKey: assetKeys.assets(), queryFn: createQueryRequest(api.core.getAssets) },
      { queryKey: org2assets.org(createRelationQueryData(ResourceType.Asset, orgId)), queryFn: createQueryRequest(api.core.findRelationships) },
      { queryKey: org2assets.org(createOrgOwnerRelationQueryData(ResourceType.Asset)), queryFn: createQueryRequest(api.core.findRelationships) }
    ]
  });

  const assets = useRelationData({
    isLoading: assetsQuery.isLoading || assetsRelationQuery.isLoading || assetsOwnerQuery.isLoading,
    resources: assetsQuery.data,
    resourceRelations: assetsRelationQuery.data,
    ownedRelations: assetsOwnerQuery.data,
    resourceType: ResourceType.Asset
  });

  const { deleteRelations } = useRelations({
    refetchFn: assetsRelationQuery.refetch,
    queryKey: org2assets.org(createRelationQueryData(ResourceType.Asset)),
    onError: addErrorAlert
  });

  const assetRelationsMutation = useMutation({
    mutationFn: (relation) => {
      return api.core.createRelationship(null, relation);
    },
    onSuccess: assetsRelationQuery.refetch
  });

  const updateAssetRefetchFn = (data) => {
    const viewerRelation = 'viewer';
    const updatedRelations = _.map(data, assetRelation => {
      const asset = _.find(assets, asset => asset.id === assetRelation.resourceId);
      const assetModelRelation = {
        resourceType: 'model',
        resourceId: asset.modelId,
        relation: viewerRelation,
        subjectType: assetRelation.subjectType,
        subjectId: assetRelation.subjectId
      };
      const providerClientsList = getProviderClientListFromAsset(asset);
      const assetProvidersRelations = _.map(providerClientsList, providerClient => {
        return [
          {
            resourceType: 'provider',
            resourceId: providerClient.providerId,
            relation: viewerRelation,
            subjectType: assetRelation.subjectType,
            subjectId: assetRelation.subjectId
          },
          {
            resourceType: 'provider_client',
            resourceId: getFullProviderClientId(providerClient.providerId, providerClient.providerClientId),
            relation: viewerRelation,
            subjectType: assetRelation.subjectType,
            subjectId: assetRelation.subjectId
          }
        ];
      });
      return [
        assetRelation,
        assetModelRelation,
        ..._.flatten(assetProvidersRelations)
      ];
    });
    assetRelationsMutation.mutate(_.unionBy(_.flatten(updatedRelations), 'resourceId'));
  };

  return (
    <ContentPage
      breadcrumb={breadcrumbs}
    >
      { alerts }
      <RelationTable
        orgId={orgId}
        resources={assets}
        visibleColumns={[VisibleColumns.displayName, VisibleColumns.id, VisibleColumns.type, VisibleColumns.relation]}
        isLoading={assetsQuery.isLoading || assetsRelationQuery.isLoading || assetsOwnerQuery.isLoading}
        updateRelationsFn={updateAssetRefetchFn}
        deleteRelationsFn={deleteRelations.mutate}
        onError={addErrorAlert}
      />
    </ContentPage>
  );
};

export default OrgToAssetRelations;
